import type { MapViewSlice, GeoDataStateCreator, MapStateInfo, GeoDataState } from './types'; // Importa GeoDataState
import { LngLatBounds } from 'maplibre-gl'; // Importa LngLatBounds direttamente

export const createMapViewSlice: GeoDataStateCreator<MapViewSlice> = (set) => ({
  mapState: {
    latitude: 45.4642, // Default latitude (e.g., Milan)
    longitude: 9.1900, // Default longitude (e.g., Milan)
    zoom: 5, // Adjusted default zoom
    pitch: 0,
    bearing: 0,
    bounds: null, // Inizializza a null
  },
  layerIdToZoomTo: null, // Aggiunto stato per lo zoom
  zoomToLayerExtent: (layerId: string) => { // Aggiungo { qui
     set({ layerIdToZoomTo: layerId });
   }, // Mantengo la virgola
  clearZoomToLayerExtent: () =>
    set({ layerIdToZoomTo: null }), // Corretta sintassi set

  // Stato iniziale per il popup fisso
  isFixedPopupVisible: false,
  fixedPopupProperties: null,
  fixedPopupSelectedFields: null,
  fixedPopupLayerId: null, // Aggiunto stato iniziale per layerId

  // Azioni per il popup fisso
  showFixedPopup: (properties, selectedFields, layerId) => set({ // Aggiunto layerId come parametro
    isFixedPopupVisible: true,
    fixedPopupProperties: properties,
    fixedPopupSelectedFields: selectedFields,
    fixedPopupLayerId: layerId, // Imposta il layerId
  }),
  hideFixedPopup: () => set({
    isFixedPopupVisible: false,
    fixedPopupProperties: null,
    fixedPopupSelectedFields: null,
    fixedPopupLayerId: null, // Resetta anche il layerId
  }),

  // Stato Terrain 3D
  isTerrainActive: false,
  terrainSourceUrl: 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png', // AWS Terrarium tiles
  terrainExaggeration: 1.0,
  mapInstance: null, // Aggiunto stato per l'istanza della mappa

  // Azioni Terrain 3D
  setTerrainActive: (isActive: boolean) => set({ isTerrainActive: isActive }),
  setTerrainExaggeration: (exaggeration: number) => set({ terrainExaggeration: exaggeration }),
  setMapInstance: (instance: maplibregl.Map | null) => set({ mapInstance: instance }), // Aggiunta azione per impostare l'istanza mappa

  // Stato e Azioni per il controllo del disegno sulla mappa
  isDrawingActive: false,
  startMapDrawing: () => set({ isDrawingActive: true }),
  stopMapDrawing: () => set({ isDrawingActive: false }),

  updateMapState: (newState: Partial<MapStateInfo>) =>
    set((state) => {
      // Gestione specifica per LngLatBounds per assicurare che sia un'istanza valida
      let updatedBounds = state.mapState.bounds;
      if (newState.bounds !== undefined) {
        // Se i nuovi bounds sono null o un'istanza valida, usali.
        // Altrimenti, se sono un oggetto semplice (da JSON), tenta di reidratarli.
        if (newState.bounds === null || newState.bounds instanceof LngLatBounds) {
          updatedBounds = newState.bounds;
        } else if (typeof newState.bounds === 'object' && (newState.bounds as any)._sw && (newState.bounds as any)._ne) {
           try {
             // Tenta di creare una nuova istanza LngLatBounds dai dati semplici
             updatedBounds = new LngLatBounds(
               (newState.bounds as any)._sw,
               (newState.bounds as any)._ne
             );
           } catch (error) {
             console.error("Failed to rehydrate LngLatBounds:", error);
             updatedBounds = null; // Resetta a null in caso di errore
           }
        } else {
            updatedBounds = null; // Resetta se il formato non è riconosciuto
        }
      }

      return {
        mapState: {
          ...state.mapState,
          ...newState,
          bounds: updatedBounds, // Usa i bounds aggiornati/reidratati
        },
      };
  }),
});

// Selettore per ottenere lo stato corrente della vista mappa (lat, lon, zoom, pitch, bearing)
// Escludiamo 'bounds' perché non è necessario per l'inizializzazione della preview
// e per evitare problemi di serializzazione/deserializzazione se bounds fosse incluso.
export const selectMapView = (state: GeoDataState): Omit<MapStateInfo, 'bounds'> => { // Accetta GeoDataState
  const { bounds, ...viewState } = state.mapState; // Accede direttamente
  return viewState;
};